import React, { useEffect, useState } from "react";
import { Modal, Button, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";

const INACTIVITY_TIMEOUT =
  process.env.REACT_APP_ENVIRONMENT === "development"
    ? Number.MAX_SAFE_INTEGER // no auto-logout in development environment
    : 30 * 60 * 1000; 
const WARNING_TIME = 60 * 1000; // 1 minute warning
const CHECK_FOR_ACTIVITY = 30 * 1000; // every 30 seconds
const LOGOUT_SAVE_GRACE = 10 * 1000; // allow registered draft saves to finish

const InactivityMonitor = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const navigate = useNavigate();

  const updateActivity = () => {
    setLastActivity(Date.now());
    setShowWarning(false);
  };

  const handleStayActive = () => {
    updateActivity();
  };

  const handleLogout = async () => {
    // Allow components with unsaved work to register asynchronous saves before
    // authentication is cleared. This is especially important for onboarding
    // program drafts, where media/text saves can take longer than a fixed delay.
    const pendingSaves = [];
    const event = new CustomEvent("beforeLogout", {
      detail: {
        waitUntil: (promise) => {
          if (promise && typeof promise.then === "function") {
            pendingSaves.push(Promise.resolve(promise));
          }
        },
      },
    });
    window.dispatchEvent(event);

    if (pendingSaves.length > 0) {
      await Promise.race([
        Promise.allSettled(pendingSaves),
        new Promise((resolve) => setTimeout(resolve, LOGOUT_SAVE_GRACE)),
      ]);
    }

    setShowWarning(false);
    setIsLoggedOut(true);
    authService.logout(true);
  };

  useEffect(() => {
    if (!authService.isAuthenticated() && !isLoggedOut) return;

    const intervalId = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;

      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        handleLogout();
      } else if (timeSinceLastActivity >= INACTIVITY_TIMEOUT - WARNING_TIME) {
        setShowWarning(true);
      }
    }, CHECK_FOR_ACTIVITY);

    const activityHandler = () => {
      updateActivity();
    };

    // Typing in tutorial fields is real activity too. Click-only tracking could
    // incorrectly time a therapist out while they were actively entering a
    // program or activity.
    window.addEventListener("click", activityHandler, { passive: true });
    window.addEventListener("keydown", activityHandler, { passive: true });
    window.addEventListener("touchstart", activityHandler, { passive: true });

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("click", activityHandler);
      window.removeEventListener("keydown", activityHandler);
      window.removeEventListener("touchstart", activityHandler);
    };
  }, [lastActivity, isLoggedOut]);

  if (isLoggedOut) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <Card className="text-center shadow-sm" style={{ maxWidth: "400px" }}>
          <Card.Body className="p-4">
            <h4 className="mb-3">Session Expired</h4>
            <p className="text-muted mb-4">
              Your session has expired due to inactivity. Please log in again to
              continue.
            </p>
            <Button
              variant="primary"
              onClick={() => navigate("/login")}
              className="w-100"
            >
              Return to Login
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!authService.isAuthenticated() && !isLoggedOut) return null;

  return (
    <Modal show={showWarning} centered>
      <Modal.Header>
        <Modal.Title>Session Timeout Warning</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Your session will expire in 1 minute due to inactivity. Would you like
        to stay logged in?
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleLogout}>
          Logout
        </Button>
        <Button variant="primary" onClick={handleStayActive}>
          Stay Active
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InactivityMonitor;
